import { useCallback, useEffect, useState } from 'react';
import { SxmBooking } from '@/features/sales/types';
import api from '@/utils/api';
import { useAutomationUserContext } from './useAutomationUserContext';
import { getErrorMessage } from '@/utils/error';

export const useSxmBookings = (enabled: boolean = false, page: number = 1, limit: number = 10) => {
    const { user, targetUserId, isInternalUser } = useAutomationUserContext();
    const [bookings, setBookings] = useState<SxmBooking[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchBookings = useCallback(async () => {
        if (!enabled) return;
        setLoading(true);
        setError(null);
        try {
            const endpoint = isInternalUser && targetUserId !== user?.id ? '/sxm/internal/bookings' : '/sxm/bookings';
            const { data } = await api.get(endpoint, {
                params: {
                    userId: targetUserId,
                    page,
                    limit
                }
            });
            // Handle the paginated structure { data: [], total: 0 } or direct array
            if (data && Array.isArray(data.data)) {
                setBookings(data.data);
                setTotal(data.total);
            } else if (Array.isArray(data)) {
                setBookings(data);
                setTotal(data.length);
            } else {
                setBookings([]);
                setTotal(0);
            }
        } catch (err: unknown) {
            console.error('Failed to fetch bookings', err);
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [targetUserId, enabled, isInternalUser, user?.id, page, limit]);

    const cancelBooking = async (id: string): Promise<void> => {
        try {
            await api.post(`/sxm/bookings/${id}/cancel`, {}, { params: { userId: targetUserId } });

            // Update local state - mark as cancelled
            setBookings(prev => prev.map(booking =>
                booking.id === id
                    ? { ...booking, status: 'cancelled' }
                    : booking
            ));
        } catch (err: unknown) {
            throw new Error(getErrorMessage(err));
        }
    };

    const createBooking = async (dto: Record<string, unknown>): Promise<SxmBooking> => {
        try {
            const { data } = await api.post('/sxm/bookings', dto, { params: { userId: targetUserId } });
            if (enabled) {
                await fetchBookings();
            } else {
                setBookings(prev => [...prev, data]);
            }
            return data;
        } catch (err: unknown) {
            throw new Error(getErrorMessage(err));
        }
    };

    const updateBooking = async (id: string, dto: Record<string, unknown>): Promise<SxmBooking> => {
        try {
            const { data } = await api.patch(`/sxm/bookings/${id}`, dto, { params: { userId: targetUserId } });
            setBookings(prev => prev.map(booking => booking.id === id ? data : booking));
            return data;
        } catch (err: unknown) {
            throw new Error(getErrorMessage(err));
        }
    };

    const deleteBooking = async (id: string): Promise<void> => {
        try {
            await api.delete(`/sxm/bookings/${id}`, { params: { userId: targetUserId } });
            setBookings(prev => prev.filter(booking => booking.id !== id));
        } catch (err: unknown) {
            throw new Error(getErrorMessage(err));
        }
    };

    useEffect(() => {
        if (enabled) {
            fetchBookings();
        }
    }, [enabled, fetchBookings]);

    return {
        bookings,
        total,
        loading,
        error,
        cancelBooking,
        createBooking,
        updateBooking,
        deleteBooking,
        refetch: fetchBookings,
    };
};
